import React, { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { emailService, type EmailSettingsDto, type EmailTemplateSummaryDto } from '../../services/emailService';

export function extractTemplates(response: unknown): EmailTemplateSummaryDto[] {
  if (Array.isArray(response)) return response;
  const data = (response as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? data : [];
}

export const EmailSettings: React.FC = () => {
  const [settings, setSettings] = useState<EmailSettingsDto | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'configuration' | 'templates'>('configuration');
  const [recipient, setRecipient] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configuration, templateResponse] = await Promise.all([emailService.getSettings(), emailService.getTemplates()]);
      setSettings(configuration);
      setTemplates(extractTemplates(templateResponse));
      setRecipient(configuration.fromEmail || '');
    } catch {
      setError('Unable to load email configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadData(); }, []);

  const sendTest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (testing || !recipient.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await emailService.testSystem({ testEmailAddress: recipient.trim() });
      setTestResult({
        success: result.isSuccess,
        message: result.isSuccess
          ? 'The email provider accepted the test message. Check the destination inbox to confirm delivery.'
          : (result.errors?.join(' ') || 'The test message could not be sent.'),
      });
    } catch {
      setTestResult({ success: false, message: 'Unable to send the test message. Please try again.' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <p className="p-6 text-gray-600">Loading email configuration...</p>;

  return <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900">Email Settings</h2>
      <p className="mt-1 text-gray-600">Email configuration and templates are managed with the site deployment. Contact your administrator to change them.</p>
    </div>
    {error && <div role="alert" className="rounded border border-red-200 bg-red-50 p-4 text-red-800">
      <p>{error}</p><Button variant="outline" className="mt-2" onClick={() => void loadData()}>Retry</Button>
    </div>}
    <nav aria-label="Email settings sections" className="flex flex-wrap gap-3 border-b border-gray-200 pb-3">
      <Button variant={activeTab === 'configuration' ? 'primary' : 'outline'} onClick={() => setActiveTab('configuration')}>Configuration</Button>
      <Button variant={activeTab === 'templates' ? 'primary' : 'outline'} onClick={() => setActiveTab('templates')}>Email Templates</Button>
    </nav>
    {activeTab === 'configuration' && settings && <>
      <Card className="p-6">
        <dl className="grid gap-5 sm:grid-cols-2">
          {[
            ['Outbound email', settings.isEnabled ? 'Enabled' : 'Disabled'],
            ['Sender name', settings.fromName || 'Not configured'],
            ['Sender address', settings.fromEmail || 'Not configured'],
            ['Support address', settings.supportEmail || 'Not configured'],
          ].map(([label, value]) => <div key={label}><dt className="text-sm text-gray-500">{label}</dt><dd className="mt-1 break-words font-medium text-gray-900">{value}</dd></div>)}
        </dl>
      </Card>
      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900">Send a test email</h3>
        <p className="mt-1 text-sm text-gray-600">This sends a real message using the deployed email configuration. Delivery follows any configured recipient restrictions or test redirects.</p>
        <form onSubmit={sendTest} className="mt-4 space-y-4">
          <Input type="email" label="Test recipient" value={recipient} onChange={event => setRecipient(event.target.value)} required disabled={testing} />
          <Button type="submit" disabled={testing || !settings.isEnabled || !recipient.trim()}>{testing ? 'Sending...' : 'Send test email'}</Button>
        </form>
        {testResult && <p role={testResult.success ? 'status' : 'alert'} className={`mt-4 rounded p-3 ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>{testResult.message}</p>}
      </Card>
    </>}
    {activeTab === 'templates' && <Card className="p-6">
      <h3 className="text-lg font-medium text-gray-900">Deployment-managed templates</h3>
      {templates.length ? <ul className="mt-4 divide-y divide-gray-200">{templates.map(template => <li key={template.name} className="py-3">{template.displayName || template.name}</li>)}</ul>
        : <p className="mt-3 text-sm text-gray-600" data-testid="templates-empty">No custom template files are deployed. Standard ticket emails use the application's built-in templates.</p>}
    </Card>}
  </div>;
};